/**
 * El bloque de voz de la agencia, compuesto en el cliente a partir del BORRADOR del formulario.
 *
 * Por qué existe: el `preview` que devuelve el backend es el del último guardado. Mientras la
 * agencia edita, el panel de vista previa mostraba el bloque VIEJO — o sea que el usuario creía
 * estar leyendo "lo que va a leer la IA" justo cuando no lo era. Componer el borrador acá es la
 * única forma de contestar esa pregunta sin un endpoint de preview.
 *
 * ⚠️ **Es una RÉPLICA de `agent_tools/services/agency_voice_service.py` del Backend** (orden de las
 * líneas, copy literal, sanitizado y topes). El backend sigue siendo la autoridad: al guardar, el
 * componente vuelve a mostrar el `preview` que él devuelve. Si allá cambia una frase, cambiarla
 * también acá — el guardián es `__tests__/agencyVoicePreview.test.js`.
 *
 * El texto NO se traduce: es el prompt literal que recibe la IA, no copy de la interfaz.
 */

export const MAX_VOICE_BLOCK_CHARS = 1200;
export const MAX_EXTRA_GUIDELINES_CHARS = 600;

export const VOICE_BLOCK_PREAMBLE = 'Preferencias de comunicación definidas por la agencia para '
    + 'este asistente. Complementan y NUNCA anulan las reglas operativas de Dashbo '
    + '(solo lectura sobre campañas, no inventar datos, escritura solo con confirmación explícita).';

const TONE_LINES = {
    CERCANO: 'Tono: cercano y claro, sin jerga técnica.',
    FORMAL: 'Tono: formal y profesional.',
    EJECUTIVO: 'Tono: ejecutivo — directo, breve, primero la conclusión.',
};

const ALERT_LINES = {
    SUAVIZAR: 'Al hablar de alertas, desvíos o problemas: presentarlos con calma, sin lenguaje de '
        + 'urgencia ni de error, y enfocarse en los próximos pasos.',
    DERIVAR_A_AGENCIA: 'Al detectar alertas, desvíos o problemas: no entrar en detalle; sugerir '
        + 'conversarlo con el equipo de la agencia, que ya lo está mirando.',
};

/** Espejo de `sanitize_agency_free_text`: saca lo que rompería el render de los prompts que
 *  envuelven al bloque (tokens `{{...}}`, marcadores de sección, fences) y lo acota. */
export function sanitizeAgencyFreeText(text) {
    if (!text) return '';
    let cleaned = String(text);
    ['{{', '}}', '<!--', '-->', '```'].forEach((sequence) => {
        cleaned = cleaned.split(sequence).join('');
    });
    cleaned = cleaned
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line)
        .join('\n');
    return cleaned.slice(0, MAX_EXTRA_GUIDELINES_CHARS).trim();
}

/** Espejo de `AgencyVoiceConfig.is_configured()`: ¿hay algo que inyectar? */
export function isVoiceConfigured({tone, alertHandling, topics, guidelines}) {
    return Boolean(
        tone
        || (topics || []).length
        || (alertHandling && alertHandling !== 'NORMAL')
        || (guidelines || '').trim(),
    );
}

/**
 * El bloque tal como lo armaría el backend con estos valores, o '' si no hay nada configurado.
 * El orden es el del servicio: preámbulo fijo → tono → manejo de alertas → temas a evitar →
 * reglas adicionales.
 */
export function buildVoicePreview({tone, alertHandling, topics, guidelines} = {}) {
    if (!isVoiceConfigured({tone, alertHandling, topics, guidelines})) return '';

    const lines = [VOICE_BLOCK_PREAMBLE];

    const toneLine = TONE_LINES[tone];
    if (toneLine) lines.push(toneLine);

    const alertLine = ALERT_LINES[alertHandling];
    if (alertLine) lines.push(alertLine);

    const cleanTopics = (topics || []).map((topic) => (topic || '').trim()).filter((topic) => topic);
    if (cleanTopics.length) {
        lines.push(`Temas que este asistente no debe tratar: ${cleanTopics.join(', ')}. `
            + 'Si el usuario pregunta por ellos, sugerir conversarlo con la agencia.');
    }

    const extra = sanitizeAgencyFreeText(guidelines);
    if (extra) lines.push(`Indicaciones adicionales de la agencia:\n${extra}`);

    return lines.join('\n').slice(0, MAX_VOICE_BLOCK_CHARS).trim();
}
